import type { ReactNode } from "react";
import {
  BarChart3,
  Binoculars,
  BookOpen,
  LayoutTemplate,
  Route,
  Scale,
  SearchCheck,
  Sprout,
  TextCursorInput,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { SlideDef, SlideProps } from "@/engine/types";
import { GradientText, Reveal, RevealItem, RevealList, SlideShell } from "@/ui";
import { TopicCard } from "./components/TopicCard";

interface Source {
  icon: LucideIcon;
  label: string;
}

const SOURCES: readonly Source[] = [
  { icon: Sprout, label: "Seed terms" },
  { icon: Binoculars, label: "Competitor pages" },
  { icon: TextCursorInput, label: "Autocomplete" },
  { icon: SearchCheck, label: "Related searches" },
  { icon: BarChart3, label: "Your own Search Console queries" },
];

function TypeChip({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xl text-white/85">
      <Icon className="h-5 w-5 text-white/60" />
      {children}
    </span>
  );
}

function NicheKeywordsSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={5} title="Finding niche keywords">
      <RevealList
        active={active}
        stagger={0.18}
        className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-8"
      >
        <RevealItem className="h-full">
          <TopicCard icon={Sprout} title="Where ideas come from">
            <ul className="flex flex-col gap-3.5">
              {SOURCES.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-4 text-2xl text-white/85">
                  <Icon className="h-6 w-6 shrink-0 text-white/50" strokeWidth={1.75} />
                  {label}
                </li>
              ))}
            </ul>
          </TopicCard>
        </RevealItem>

        <RevealItem className="h-full">
          <TopicCard icon={Route} title="Map intent → content type">
            <p className="text-2xl leading-relaxed text-white/70">
              Map each keyword to an <span className="font-semibold text-white">intent</span> and a{" "}
              <span className="font-semibold text-white">content type</span>:
            </p>
            <div className="flex flex-wrap gap-3">
              <TypeChip icon={BookOpen}>guide</TypeChip>
              <TypeChip icon={Scale}>comparison</TypeChip>
              <TypeChip icon={Wrench}>tool</TypeChip>
              <TypeChip icon={LayoutTemplate}>landing page</TypeChip>
            </div>
            <p className="mt-auto text-2xl leading-relaxed text-white/70">
              Not every keyword needs a{" "}
              <span className="font-semibold text-white">new URL</span>.
            </p>
          </TopicCard>
        </RevealItem>
      </RevealList>

      <Reveal active={active} delay={0.7} className="mt-12 border-t border-white/10 pt-8 text-center">
        <p className="font-display text-4xl leading-snug">
          <GradientText className="font-semibold">No tool guarantees ranking</GradientText>
          <span className="text-white/85">
            {" "}
            — research tells you where you can credibly be the best answer.
          </span>
        </p>
      </Reveal>
    </SlideShell>
  );
}

export const nicheKeywords: SlideDef = {
  id: "c5-niche-keywords",
  chapter: 5,
  title: "Finding niche keywords",
  component: NicheKeywordsSlide,
};
